import React, { useEffect, useState, useSyncExternalStore } from 'react';
import { Avatar } from 'antd';
import api from '../api';

// A user's picture (routes/avatars.py), or their initials in a colour of their
// own when they have none.
//
// Every user's picture version is loaded once and shared; each picture is
// fetched once per version (with the sign-in token, so not a plain <img src>)
// and kept for the rest of the visit.

let versions = {};
let versionsRequest = null;
const listeners = new Set();
const pictures = new Map();   // `${userId}:${version}` -> object URL, or the request for it

export function loadAvatars(force = false) {
  if (!versionsRequest || force) {
    versionsRequest = api.get('/api/avatars')
      .then((r) => { versions = r.data || {}; listeners.forEach((f) => f()); })
      .catch(() => {});
  }
  return versionsRequest;
}

const subscribe = (f) => { listeners.add(f); return () => listeners.delete(f); };

function usePicture(userId) {
  const version = useSyncExternalStore(subscribe, () => (userId ? versions[userId] : undefined));
  const key = version ? `${userId}:${version}` : null;
  const [url, setUrl] = useState(() => (typeof pictures.get(key) === 'string' ? pictures.get(key) : null));

  useEffect(() => { loadAvatars(); }, []);
  useEffect(() => {
    if (!key) { setUrl(null); return undefined; }
    let live = true;
    let pic = pictures.get(key);
    if (!pic) {
      pic = api.get(`/api/avatars/${userId}`, { params: { v: version }, responseType: 'blob' })
        .then((r) => { const u = URL.createObjectURL(r.data); pictures.set(key, u); return u; })
        .catch(() => { pictures.delete(key); return null; });
      pictures.set(key, pic);
    }
    Promise.resolve(pic).then((u) => { if (live) setUrl(u); });
    return () => { live = false; };
  }, [key, userId, version]);
  return url;
}

const COLOURS = ['#e8682a', '#1677ff', '#389e0d', '#722ed1', '#c41d7f', '#08979c', '#d48806', '#2f54eb'];

export function initialsOf(name) {
  const words = (name || '').replace(/\(.*?\)/g, '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return ((words[0][0] || '') + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
}

export default function UserAvatar({ userId, name, size = 24, style }) {
  const url = usePicture(userId);
  let h = 0;
  for (const ch of String(userId || name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return (
    <Avatar
      size={size}
      src={url || undefined}
      alt={name || ''}
      style={{
        flexShrink: 0,
        fontSize: Math.max(10, Math.round(size * 0.42)),
        background: url ? undefined : COLOURS[h % COLOURS.length],
        ...style,
      }}
    >
      {initialsOf(name)}
    </Avatar>
  );
}

// The picture then the name, on one line (lists, panels, dropdowns).
export function UserName({ userId, name, size = 20, gap = 6, children }) {
  if (!name) return children ?? null;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap, minWidth: 0, maxWidth: '100%', verticalAlign: 'middle' }}>
      <UserAvatar userId={userId} name={name} size={size} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{children ?? name}</span>
    </span>
  );
}
